import type { UpdateUserBody, User } from '@api/models';
import { CreateUserBodySex } from '@api/models';

import { ApiClient } from '@website/lib/api';

/**
 * Update the current user. The backend infers userId from the session cookie when called via proxy,
 * but some endpoints still require the id param; we therefore accept userId optionally.
 *
 * @param updateUserBody - The user data to update (must include id)
 */
export async function updateUser(updateUserBody: UpdateUserBody & { id?: string }): Promise<User> {
  // If id is provided, use the explicit variant; otherwise, try to fetch session-less id
  // The generated client requires an id param; components typically know the user id or can fetch session.
  if (!('id' in updateUserBody) || !updateUserBody.id) {
    // In most usages, components have the `user` prop; enforce the caller to pass id if needed.
    throw new Error('updateUser requires an id on the client. Pass the current user id.');
  }
  const { id, ...body } = updateUserBody;
  return ApiClient.updateUser(id, body);
}

/**
 * Remove the current user's profile picture.
 */
export async function removeProfilePicture(): Promise<void> {
  await ApiClient.removeProfilePicture();
}

export interface CreateUserData {
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  role?: string;
  bio?: string;
  profilePictureFile?: File;
  socialMediaLinks?: {
    website?: string;
    instagram?: string;
    twitter?: string;
    facebook?: string;
  };
}

/**
 * Create a new user account
 *
 * @param userData - The user data for account creation
 */
export async function createUser(userData: CreateUserData): Promise<User> {
  const response = await ApiClient.createUser({
    firstName: userData.firstName,
    lastName: userData.lastName,
    email: userData.email,
    mobileNumber: userData.mobileNumber,
    isProfessional: userData.role === 'professional',
    accountCreationDate: Date.now(),
    bio: userData.bio ? { en: userData.bio, fr: userData.bio, es: userData.bio } : undefined,
    sex: CreateUserBodySex.other // Default since we don't collect this in the form
  });

  return response;
}
